import type { ReactNode } from 'react'

type Action = {
  label: string
  variant: 'primary' | 'outline' | 'muted'
  disabled?: boolean
  onClick: () => void
}

const CARD =
  'w-full max-w-sm self-start overflow-hidden rounded-xl border border-stone-200 bg-stone-100 shadow-[0_1px_2px_rgba(28,25,23,0.06)]'
const BUTTON = 'h-7 rounded-md px-2.5 text-[11px] font-medium transition disabled:cursor-not-allowed disabled:opacity-60'

const VARIANTS = {
  primary:
    'bg-linear-to-b from-stone-700 to-stone-950 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_1px_2px_rgba(28,25,23,0.2)] hover:opacity-90',
  outline: 'border border-stone-200 bg-stone-100 text-stone-700 hover:bg-stone-200',
  muted: 'text-stone-500 hover:bg-stone-200 hover:text-stone-700',
}

type Props = {
  icon: ReactNode
  title: ReactNode
  description?: ReactNode
  children?: ReactNode
  actions: Action[]
}

/** A card in the message stream that asks the user to choose before the turn continues. */
export default function ChoicePrompt({ icon, title, description, children, actions }: Props) {
  return (
    <div className={CARD}>
      <div className="flex gap-2.5 p-3">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-stone-200">{icon}</span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-stone-900">{title}</p>
          {description && <p className="mt-0.5 text-[11px] leading-snug text-stone-500">{description}</p>}
          {children}
        </div>
      </div>

      <div className="flex flex-wrap justify-end gap-1.5 border-t border-stone-200 px-3 py-2">
        {actions.map((action) => (
          <button
            key={action.label}
            type="button"
            disabled={action.disabled}
            onClick={action.onClick}
            className={`${BUTTON} ${VARIANTS[action.variant]}`}
          >
            {action.label}
          </button>
        ))}
      </div>
    </div>
  )
}
